// src/features/idealWeek/pages/ProfileIdealWeek.tsx
// Ideal Week -- 展示/編輯分離的週曆設定頁面
// 桌面：卡片點擊展開 panel；行動：Bottom Sheet

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, AlertCircle, Plus, X } from 'lucide-react';
import { useHeader } from '../../../hooks/useHeader';
import { useAuth } from '../../../contexts/AuthContext';
import { useIdealWeek } from '../hooks/useIdealWeek';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { IdealWeek } from '../types/idealWeek';
import { markInsightSelfAdjusted } from '../../../services/api/insight';
import { logger } from '../../../utils/logger';

type SaveStatus = 'idle' | 'success' | 'error';

// 星期定義：從 Monday 開始
const DAYS = [
  { key: 1, label: 'Mon', full: 'Monday' },
  { key: 2, label: 'Tue', full: 'Tuesday' },
  { key: 3, label: 'Wed', full: 'Wednesday' },
  { key: 4, label: 'Thu', full: 'Thursday' },
  { key: 5, label: 'Fri', full: 'Friday' },
  { key: 6, label: 'Sat', full: 'Saturday' },
  { key: 0, label: 'Sun', full: 'Sunday' },
] as const;

const PLACEHOLDERS: Record<number, string> = {
  0: 'Rest & Recharge',
  1: 'Deep Work',
  2: 'Creative Day',
  3: 'Meetings',
  4: 'Learning',
  5: 'Admin & Review',
  6: 'Personal Time',
};

const DESC_PLACEHOLDERS: Record<number, string> = {
  0: 'e.g., slow mornings, no screens, outdoor time',
  1: 'e.g., focused coding, no meetings before noon',
  2: 'e.g., brainstorming, design, writing',
  3: 'e.g., syncs, 1:1s, client calls',
  4: 'e.g., courses, reading, side projects',
  5: 'e.g., email, planning, weekly review',
  6: 'e.g., family, hobbies, social events',
};

const DAY_COLORS: Record<number, {
  gradient: string;
  ring: string;
  label: string;
  dot: string;
  bg: string;
  lightBg: string;
}> = {
  1: { gradient: 'from-violet-500/10 to-sky-500/5', ring: 'ring-violet-400', label: 'text-violet-600', dot: 'bg-violet-400', bg: 'bg-violet-500', lightBg: 'bg-violet-50' },
  2: { gradient: 'from-sky-500/10 to-sky-500/5', ring: 'ring-sky-400', label: 'text-sky-600', dot: 'bg-sky-400', bg: 'bg-sky-500', lightBg: 'bg-sky-50' },
  3: { gradient: 'from-sky-500/10 to-sky-500/5', ring: 'ring-sky-400', label: 'text-sky-600', dot: 'bg-sky-400', bg: 'bg-sky-500', lightBg: 'bg-sky-50' },
  4: { gradient: 'from-sky-500/10 to-violet-500/5', ring: 'ring-sky-400', label: 'text-sky-600', dot: 'bg-sky-400', bg: 'bg-sky-500', lightBg: 'bg-sky-50' },
  5: { gradient: 'from-sky-500/10 to-fuchsia-500/5', ring: 'ring-sky-400', label: 'text-sky-600', dot: 'bg-sky-400', bg: 'bg-sky-500', lightBg: 'bg-sky-50' },
  6: { gradient: 'from-amber-500/10 to-orange-500/5', ring: 'ring-amber-400', label: 'text-amber-600', dot: 'bg-amber-400', bg: 'bg-amber-500', lightBg: 'bg-amber-50' },
  0: { gradient: 'from-emerald-500/10 to-teal-500/5', ring: 'ring-emerald-400', label: 'text-emerald-600', dot: 'bg-emerald-400', bg: 'bg-emerald-500', lightBg: 'bg-emerald-50' },
};

// ---- DayCard（純展示，桌面版用）----
interface DayCardProps {
  dayKey: number;
  fullName: string;
  title: string;
  description: string;
  isSelected: boolean;
  index: number;
  onClick: () => void;
}

const DayCard: React.FC<DayCardProps> = ({
  dayKey,
  fullName,
  title,
  description,
  isSelected,
  index,
  onClick,
}) => {
  const isWeekend = dayKey === 0 || dayKey === 6;
  const color = DAY_COLORS[dayKey];
  const hasContent = title.trim().length > 0;

  return (
    <motion.button
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
      onClick={onClick}
      aria-label={`Edit theme for ${fullName}`}
      aria-expanded={isSelected}
      className={`
        group relative flex flex-col w-full text-left
        rounded-xl border transition-all duration-200
        bg-gradient-to-br ${color.gradient}
        ${isSelected
          ? `border-transparent ring-2 ${color.ring} shadow-lg`
          : `border-neutral-200/60 hover:border-neutral-300 hover:shadow-md`
        }
        ${isWeekend ? 'border-dashed' : ''}
        min-h-[100px] p-3 cursor-pointer
      `}
    >
      {/* 星期標籤 */}
      <div className="flex items-center gap-1.5 mb-2">
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${color.dot}`} />
        <span className={`text-xs font-semibold tracking-wide uppercase ${color.label}`}>
          {fullName}
        </span>
      </div>

      {/* 內容或空白提示 */}
      {hasContent ? (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-neutral-800 leading-snug truncate">
            {title}
          </p>
          {description && (
            <p className="text-xs text-neutral-500 mt-1 line-clamp-2 leading-relaxed">
              {description}
            </p>
          )}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className={`
            w-6 h-6 rounded-full flex items-center justify-center
            transition-colors duration-200
            bg-neutral-100 group-hover:${color.lightBg}
          `}>
            <Plus size={12} className="text-neutral-400 group-hover:text-neutral-600" />
          </div>
        </div>
      )}

      {/* 選中指示箭頭 — bottom-[-6px] 讓菱形中心剛好落在卡片底部邊緣 */}
      {isSelected && (
        <div className={`absolute left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 ${color.dot}`} style={{ bottom: '-8px' }} />
      )}
    </motion.button>
  );
};

// ---- ExpandedDayPanel（桌面展開編輯面板）----
interface EditPanelProps {
  dayKey: number;
  fullName: string;
  title: string;
  description: string;
  placeholder: string;
  descPlaceholder: string;
  onTitleChange: (v: string) => void;
  onDescChange: (v: string) => void;
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
  saveStatus: SaveStatus;
}

const ExpandedDayPanel: React.FC<EditPanelProps> = ({
  dayKey,
  fullName,
  title,
  description,
  placeholder,
  descPlaceholder,
  onTitleChange,
  onDescChange,
  onSave,
  onClose,
  saving,
  saveStatus,
}) => {
  const color = DAY_COLORS[dayKey];
  const titleRef = useRef<HTMLInputElement>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, [dayKey]);

  // 讓 textarea 跟著內容自動長高
  const handleDescChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onDescChange(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  // 初始化時也要設一次高度
  useEffect(() => {
    if (descRef.current) {
      descRef.current.style.height = 'auto';
      descRef.current.style.height = `${descRef.current.scrollHeight}px`;
    }
  }, [dayKey]);

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="overflow-hidden"
    >
      {/* Panel 無 border/shadow，直接融入頁面背景；圓角只在上方，避免底部產生浮島感 */}
      <div className={`bg-white rounded-xl px-5 pt-5 pb-6 mt-5`}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${color.dot}`} />
            <span className={`text-sm font-semibold ${color.label}`}>{fullName}</span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close editor"
            className="p-1 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Title */}
          <div>
            <label className="text-sm font-medium text-neutral-600 mb-1.5 block">
              Day Theme
            </label>
            <input
              ref={titleRef}
              type="text"
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder={placeholder}
              maxLength={50}
              aria-label={`Theme for ${fullName}`}
              className={`
                w-full px-3 py-2.5 text-sm font-medium rounded-lg border
                bg-neutral-50 border-neutral-200
                focus:outline-none focus:ring-2 ${color.ring} focus:border-transparent
                placeholder:text-neutral-300 text-neutral-800
                transition-all duration-150
              `}
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-neutral-600 mb-1.5 block">
              What does this day look like? <span className="text-neutral-400 font-normal text-xs">(optional)</span>
            </label>
            <textarea
              ref={descRef}
              value={description}
              onChange={handleDescChange}
              placeholder={descPlaceholder}
              rows={2}
              aria-label={`Description for ${fullName}`}
              className={`
                w-full px-3 py-2.5 text-sm rounded-lg border resize-none overflow-hidden
                bg-neutral-50 border-neutral-200
                focus:outline-none focus:ring-2 ${color.ring} focus:border-transparent
                placeholder:text-neutral-300 text-neutral-600
                transition-all duration-150
              `}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1">
            <AnimatePresence mode="wait">
              {saveStatus === 'success' && (
                <motion.span
                  key="ok"
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-1 text-xs text-success-600 font-medium"
                >
                  <Check size={12} /> Saved
                </motion.span>
              )}
              {saveStatus === 'error' && (
                <motion.span
                  key="err"
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-1 text-xs text-error-500 font-medium"
                >
                  <AlertCircle size={12} /> Failed to save
                </motion.span>
              )}
            </AnimatePresence>

            <button
              onClick={onSave}
              disabled={saving}
              className={`
                px-4 py-1.5 text-sm font-medium rounded-lg transition-all duration-150
                ${color.bg} text-white
                hover:opacity-90 active:opacity-80
                disabled:opacity-40 disabled:cursor-not-allowed
                flex items-center gap-1.5
              `}
            >
              {saving ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ---- DayListRow（行動版列表行）----
interface DayListRowProps {
  dayKey: number;
  fullName: string;
  title: string;
  index: number;
  onClick: () => void;
}

const DayListRow: React.FC<DayListRowProps> = ({
  dayKey,
  fullName,
  title,
  index,
  onClick,
}) => {
  const isWeekend = dayKey === 0 || dayKey === 6;
  const color = DAY_COLORS[dayKey];
  const hasContent = title.trim().length > 0;

  return (
    <motion.button
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04, duration: 0.3 }}
      onClick={onClick}
      aria-label={`Edit theme for ${fullName}`}
      className="w-full flex items-center gap-3 px-4 py-3 bg-white hover:bg-neutral-50 active:bg-neutral-100 transition-colors text-left"
    >
      {/* 色塊 */}
      <div className={`w-1 self-stretch rounded-full shrink-0 ${color.dot} ${isWeekend ? 'opacity-50' : ''}`} />

      {/* 星期 + 主題（兩行垂直）*/}
      <div className="flex-1 min-w-0 py-1">
        <p className={`text-xs font-medium tracking-wide ${isWeekend ? 'text-neutral-400' : color.label}`}>
          {fullName}
        </p>
        <p className={`text-sm mt-0.5 truncate ${hasContent ? 'text-neutral-800 font-medium' : 'text-neutral-300'}`}>
          {hasContent ? title : 'Tap to set theme'}
        </p>
      </div>

      <ChevronRight size={16} className="text-neutral-300 shrink-0" />
    </motion.button>
  );
};

// ---- DayBottomSheet（行動版 Bottom Sheet）----
// 注意：用 createPortal 渲染到 document.body，必須由父層用 isDesktop 控制是否 render，
// 不能依賴 Tailwind 響應式 class（lg:hidden 對 portal 無效）
const DayBottomSheet: React.FC<EditPanelProps> = ({
  dayKey,
  fullName,
  title,
  description,
  placeholder,
  descPlaceholder,
  onTitleChange,
  onDescChange,
  onSave,
  onClose,
  saving,
  saveStatus,
}) => {
  const color = DAY_COLORS[dayKey];
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // 延遲 focus 等待動畫
    const t = setTimeout(() => titleRef.current?.focus(), 200);
    return () => clearTimeout(t);
  }, [dayKey]);

  // 鎖定 body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-modal flex flex-col justify-end">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 bg-black/40"
          onClick={onClose}
        />

        {/* Sheet */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="relative bg-white rounded-t-2xl shadow-2xl px-5 pt-4 pb-8"
        >
          {/* Handle */}
          <div className="w-10 h-1 bg-neutral-200 rounded-full mx-auto mb-5" />

          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${color.dot}`} />
              <span className={`text-base font-semibold ${color.label}`}>{fullName}</span>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="text-xs font-medium text-neutral-500 mb-1.5 block">
                Day Theme
              </label>
              <input
                ref={titleRef}
                type="text"
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
                placeholder={placeholder}
                maxLength={50}
                aria-label={`Theme for ${fullName}`}
                className={`
                  w-full px-4 py-3 text-base font-medium rounded-xl border
                  bg-neutral-50 border-neutral-200
                  focus:outline-none focus:ring-2 ${color.ring} focus:border-transparent
                  placeholder:text-neutral-300 text-neutral-800
                `}
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-medium text-neutral-500 mb-1.5 block">
                What does this day look like? <span className="text-neutral-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => onDescChange(e.target.value)}
                placeholder={descPlaceholder}
                rows={3}
                aria-label={`Description for ${fullName}`}
                className={`
                  w-full px-4 py-3 text-sm rounded-xl border resize-none
                  bg-neutral-50 border-neutral-200
                  focus:outline-none focus:ring-2 ${color.ring} focus:border-transparent
                  placeholder:text-neutral-300 text-neutral-600
                `}
              />
            </div>

            {/* Save */}
            <button
              onClick={onSave}
              disabled={saving}
              className="btn-primary w-full"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </span>
              ) : 'Save'}
            </button>

            <AnimatePresence mode="wait">
              {saveStatus === 'success' && (
                <motion.p
                  key="ok"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center justify-center gap-1.5 text-sm text-success-600 font-medium"
                >
                  <Check size={14} /> Saved
                </motion.p>
              )}
              {saveStatus === 'error' && (
                <motion.p
                  key="err"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center justify-center gap-1.5 text-sm text-error-500"
                >
                  <AlertCircle size={14} /> Failed to save. Please try again.
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

// ---- 主頁面 ----
const ProfileIdealWeek: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const userId = currentUser?.uid ?? null;
  const { idealWeek, isLoading, updateIdealWeek } = useIdealWeek(userId);
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // 從 URL 讀取 insightId（「我來調整」流程帶入）
  const insightId = searchParams.get('insightId');

  // 目前選中編輯的天（DAYS index，非 dayKey）
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  // 面板內的暫存狀態（避免每次 keystroke 都更新全域）
  const [draft, setDraft] = useState<{ title: string; description: string }>({ title: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const setDaysCount = DAYS.filter(d => idealWeek?.[d.key as keyof IdealWeek]?.title?.trim()).length;

  const openEditor = useCallback((index: number) => {
    const day = DAYS[index];
    const entry = idealWeek?.[day.key as keyof IdealWeek];
    setSelectedIndex(index);
    setDraft({ title: entry?.title || '', description: entry?.description || '' });
    setSaveStatus('idle');
  }, [idealWeek]);

  const closeEditor = useCallback(() => {
    setSelectedIndex(null);
    setSaveStatus('idle');
  }, []);

  const handleCardClick = useCallback((index: number) => {
    if (selectedIndex === index) {
      closeEditor();
    } else {
      openEditor(index);
    }
  }, [selectedIndex, openEditor, closeEditor]);

  const handleSave = useCallback(async () => {
    if (!userId || selectedIndex === null) return;

    setSaving(true);
    setSaveStatus('idle');

    // 從 idealWeek 取所有天的現有資料，只替換選中的那天
    const data: IdealWeek = {};
    DAYS.forEach((d, i) => {
      const existing = idealWeek?.[d.key as keyof IdealWeek];
      const t = i === selectedIndex ? draft.title.trim() : existing?.title?.trim();
      if (t) {
        const desc = i === selectedIndex ? draft.description.trim() : existing?.description?.trim();
        data[d.key as keyof IdealWeek] = desc ? { title: t, description: desc } : { title: t };
      }
    });

    try {
      await updateIdealWeek(data);
      setSaveStatus('success');

      // 「我來調整」流程：儲存成功後回報 insight，並清除 URL 參數
      if (insightId) {
        markInsightSelfAdjusted(insightId).catch((err) => {
          logger.error('ProfileIdealWeek', 'markInsightSelfAdjusted 失敗', err);
        });
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('insightId');
          return next;
        }, { replace: true });
      }

      setTimeout(() => {
        setSaveStatus('idle');
        closeEditor();
      }, 1200);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  }, [userId, selectedIndex, idealWeek, draft, updateIdealWeek, closeEditor, insightId, setSearchParams]);

  // Header
  const backButton = useMemo(() => (
    <button
      onClick={() => navigate('/profile')}
      className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
      aria-label="Back to profile"
    >
      <ChevronLeft size={20} className="text-neutral-600" />
    </button>
  ), [navigate]);

  useHeader({ title: 'Ideal Week', leftAction: backButton });

  if (isLoading && !idealWeek) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-sky-200 border-t-sky-500 rounded-full animate-spin" />
      </div>
    );
  }

  const selectedDay = selectedIndex !== null ? DAYS[selectedIndex] : null;

  const editPanelProps = selectedDay ? {
    dayKey: selectedDay.key,
    fullName: selectedDay.full,
    title: draft.title,
    description: draft.description,
    placeholder: PLACEHOLDERS[selectedDay.key],
    descPlaceholder: DESC_PLACEHOLDERS[selectedDay.key],
    onTitleChange: (v: string) => setDraft(prev => ({ ...prev, title: v })),
    onDescChange: (v: string) => setDraft(prev => ({ ...prev, description: v })),
    onSave: handleSave,
    onClose: closeEditor,
    saving,
    saveStatus,
  } : null;

  return (
    <div className="max-w-5xl mx-auto">
      {/* 頂部說明 + 進度 */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="px-4 py-4 flex items-center justify-between"
      >
        <p className="text-sm text-neutral-500">
          Give each day a theme to shape your weekly rhythm.
        </p>
        <span className="text-xs text-neutral-400 font-medium shrink-0 ml-3">
          {setDaysCount} / 7 set
        </span>
      </motion.div>

      {/* 桌面版：7 欄卡片 + 展開面板 */}
      <div className="hidden lg:block px-4 pb-8">
        <div className="grid grid-cols-7 gap-2.5">
          {DAYS.map((day, index) => (
            <DayCard
              key={day.key}
              dayKey={day.key}
              fullName={day.full}
              title={idealWeek?.[day.key as keyof IdealWeek]?.title || ''}
              description={idealWeek?.[day.key as keyof IdealWeek]?.description || ''}
              isSelected={selectedIndex === index}
              index={index}
              onClick={() => handleCardClick(index)}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {editPanelProps && (
            <ExpandedDayPanel key={selectedIndex} {...editPanelProps} />
          )}
        </AnimatePresence>
      </div>

      {/* 行動版：列表 */}
      <div className="lg:hidden">
        <div className="divide-y divide-neutral-100">
          {DAYS.map((day, index) => (
            <DayListRow
              key={day.key}
              dayKey={day.key}
              fullName={day.full}
              title={idealWeek?.[day.key as keyof IdealWeek]?.title || ''}
              index={index}
              onClick={() => openEditor(index)}
            />
          ))}
        </div>

        {/* Bottom Sheet — 必須用 isDesktop 判斷，不可用 lg:hidden（createPortal 脫離 DOM 樹）*/}
        {!isDesktop && (
          <AnimatePresence>
            {editPanelProps && (
              <DayBottomSheet key={selectedIndex} {...editPanelProps} />
            )}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default ProfileIdealWeek;
